/**
 * @vigie/shared — contrats partagés entre l'API (Worker) et l'app React.
 *
 * Règle : toute forme de donnée qui traverse la frontière HTTP est déclarée ici.
 * Étendre ce fichier plutôt que de dupliquer des types côté app ou côté worker.
 */

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export interface User {
  id: string;
  email: string;
  createdAt: string; // ISO 8601
}

export interface Session {
  token: string;
  userId: string;
  expiresAt: string;
}

export interface SignupRequest {
  email: string;
  password: string;
}
export interface LoginRequest {
  email: string;
  password: string;
}
export interface AuthResponse {
  user: User;
}

// ---------------------------------------------------------------------------
// Sites
// ---------------------------------------------------------------------------

/** Un site du portefeuille que l'on cherche à promouvoir. */
export interface Site {
  id: string;
  userId: string;
  name: string;
  /** Domaine canonique, sans protocole. Ex. "certfleet.net". */
  domain: string;
  /** UUID du site dans Umami — sert à l'attribution des conversions. */
  umamiWebsiteId: string | null;
  /** Ton/positionnement par défaut, hérité par les plateformes. */
  llmInstructions: string | null;
  enabled: boolean;
  createdAt: string;
}

export type SiteInput = Pick<Site, 'name' | 'domain'> &
  Partial<Pick<Site, 'umamiWebsiteId' | 'llmInstructions' | 'enabled'>>;

// ---------------------------------------------------------------------------
// Plateformes
// ---------------------------------------------------------------------------

/**
 * `automated` = collecte programmatique disponible, par API ouverte (Reddit, HN…)
 *               ou par navigateur piloté (X, LinkedIn, Instagram — voir
 *               `BROWSER_PLATFORMS`).
 * `manual`    = aucune collecte : la plateforme sert de pense-bête et de point
 *               d'entrée pour coller manuellement une discussion trouvée à la main.
 */
export type PlatformKind =
  | 'reddit'
  | 'hackernews'
  | 'stackexchange'
  | 'rss'
  | 'discourse'
  | 'bluesky'
  | 'mastodon'
  | 'twitter'
  | 'instagram'
  | 'facebook_group'
  | 'linkedin'
  | 'manual';

/**
 * Plateformes collectées via un navigateur piloté (Browser Rendering) plutôt que
 * par une API. Elles exigent une session authentifiée fournie en secret.
 * Décision opérateur du 2026-08-05, périmètre **lecture seule** — voir CLAUDE.md
 * et `src/collect/collectors/browser.ts`.
 */
export const BROWSER_PLATFORMS: readonly PlatformKind[] = [
  'twitter',
  'instagram',
  'linkedin',
] as const;

export const AUTOMATED_PLATFORMS: readonly PlatformKind[] = [
  'reddit',
  'hackernews',
  'stackexchange',
  'rss',
  'discourse',
  'bluesky',
  'mastodon',
  'twitter',
  'instagram',
  'linkedin',
] as const;

export const MANUAL_PLATFORMS: readonly PlatformKind[] = ['facebook_group', 'manual'] as const;

export interface Platform {
  id: string;
  siteId: string;
  kind: PlatformKind;
  label: string;
  /** Config spécifique au collecteur (voir PlatformConfig). */
  config: PlatformConfig;
  /** Instructions LLM au niveau plateforme (ton, registre, longueur). */
  llmInstructions: string | null;
  /** Intervalle de rafraîchissement souhaité, en minutes. Min 60 (cron horaire). */
  pollIntervalMinutes: number;
  enabled: boolean;
  createdAt: string;
}

export type PlatformConfig =
  | { kind: 'reddit' }
  | { kind: 'hackernews' }
  | { kind: 'stackexchange'; site: string } // ex. "serverfault"
  | { kind: 'rss' }
  /** Forum Discourse : `instance` est l'origine du forum, ex. "https://community.spiceworks.com". */
  | { kind: 'discourse'; instance: string }
  | { kind: 'bluesky' }
  | { kind: 'mastodon'; instance: string }
  /** Collecte navigateur : `query` est construite par le collecteur à partir des sources. */
  | { kind: 'twitter' }
  | { kind: 'instagram' }
  | { kind: 'linkedin' }
  | { kind: 'facebook_group'; reminderEveryDays: number }
  | { kind: 'manual' };

export type PlatformInput = Pick<Platform, 'kind' | 'label'> &
  Partial<Pick<Platform, 'config' | 'llmInstructions' | 'pollIntervalMinutes' | 'enabled'>>;

// ---------------------------------------------------------------------------
// Sources (subreddit, topic, flux, requête…)
// ---------------------------------------------------------------------------

export type SourceKind =
  | 'subreddit'
  | 'topic'
  | 'query'
  | 'rss_feed'
  | 'tag'
  | 'group_url';

/**
 * `prospect` : la source alimente l'inbox (scoring + alertes).
 * `intel`    : la source ne sert qu'à la veille concurrentielle — ni inbox,
 *              ni alerte, ni scoring. Les deux restent analysables côté
 *              concurrence.
 */
export type SourcePurpose = 'prospect' | 'intel';

export interface Source {
  id: string;
  platformId: string;
  purpose: SourcePurpose;
  kind: SourceKind;
  /** Ex. "sysadmin" (subreddit), "https://…/feed" (rss), "ssl monitoring" (query). */
  value: string;
  /** Instructions LLM au niveau source/topic — le plus spécifique gagne. */
  llmInstructions: string | null;
  enabled: boolean;
  /**
   * Curseur de collecte : garantit qu'on ne relit jamais l'historique déjà vu.
   * Format dépendant du collecteur (fullname Reddit, epoch, guid RSS…).
   */
  lastCursor: string | null;
  lastPolledAt: string | null;
  createdAt: string;
}

export type SourceInput = Pick<Source, 'kind' | 'value'> &
  Partial<Pick<Source, 'llmInstructions' | 'enabled' | 'purpose'>>;

// ---------------------------------------------------------------------------
// Mots-clés
// ---------------------------------------------------------------------------

export type MatchType = 'contains' | 'word' | 'regex';

/**
 * Préfiltre gratuit appliqué AVANT tout appel LLM.
 * `isNegative: true` = exclusion (si ça matche, le hit est écarté).
 * Rattaché soit à une plateforme (s'applique à toutes ses sources),
 * soit à une source précise.
 */
export interface Keyword {
  id: string;
  platformId: string | null;
  sourceId: string | null;
  term: string;
  matchType: MatchType;
  isNegative: boolean;
  createdAt: string;
}

export type KeywordInput = Pick<Keyword, 'term'> &
  Partial<Pick<Keyword, 'matchType' | 'isNegative' | 'platformId' | 'sourceId'>>;

// ---------------------------------------------------------------------------
// Hits (discussions détectées)
// ---------------------------------------------------------------------------

export type HitStatus = 'new' | 'relevant' | 'ignored' | 'drafted' | 'posted';

export interface Hit {
  id: string;
  sourceId: string;
  siteId: string;
  /** Identifiant natif chez la plateforme. UNIQUE avec sourceId → dédup. */
  externalId: string;
  url: string;
  title: string;
  bodyExcerpt: string | null;
  author: string | null;
  /** Date de publication chez la plateforme. */
  postedAt: string;
  discoveredAt: string;
  /** Score de pertinence 0-10 attribué par le LLM. null = pas encore scoré. */
  score: number | null;
  scoreReason: string | null;
  /**
   * Les deux axes du jugement. `null` = jamais scoré (source `intel`, ou LLM
   * indisponible) — à ne pas confondre avec « jugé faux ».
   *
   * `seeking` sans `onTopic` : demande sincère, mais hors de notre domaine.
   * `onTopic` sans `seeking` : notre sujet, mais personne ne demande rien —
   * sans valeur en communauté, mais matière première pour le studio social.
   */
  seeking: boolean | null;
  onTopic: boolean | null;
  status: HitStatus;
  /** true si le hit vient d'un ajout manuel (URL collée par l'utilisateur). */
  isManual: boolean;
}

// ---------------------------------------------------------------------------
// Brouillons de réponse
// ---------------------------------------------------------------------------

export interface Draft {
  id: string;
  hitId: string;
  content: string;
  /** Instructions effectivement utilisées (site + plateforme + source, fusionnées). */
  promptUsed: string;
  model: string;
  createdAt: string;
  approvedAt: string | null;
}

export interface GenerateDraftRequest {
  hitId: string;
  /** Surcharge ponctuelle des instructions, sans modifier la config. */
  instructionsOverride?: string;
}

// ---------------------------------------------------------------------------
// Publications et attribution
// ---------------------------------------------------------------------------

/**
 * Une publication effectuée MANUELLEMENT par l'humain, enregistrée ici
 * pour mesurer son rendement. L'outil ne publie jamais lui-même.
 */
export interface Post {
  id: string;
  hitId: string;
  draftId: string | null;
  /** URL du commentaire/post publié. */
  url: string | null;
  postedAt: string;
  /** URL taguée UTM remise à l'utilisateur pour qu'il l'insère dans sa réponse. */
  trackingUrl: string;
  /** Identifiant de campagne UTM, unique. Ex. "vigie_p_01H…". */
  utmCampaign: string;
  notes: string | null;
}

export type ConversionKind = 'visit' | 'signup' | 'free_tool' | 'paid';

/** Agrégat rapatrié depuis Umami pour une publication donnée. */
export interface PostMetrics {
  postId: string;
  visits: number;
  signups: number;
  freeToolUses: number;
  paid: number;
  lastSyncedAt: string | null;
}

// ---------------------------------------------------------------------------
// Analyse concurrentielle
// ---------------------------------------------------------------------------

/** Un concurrent suivi sur un site donné. */
export interface Competitor {
  id: string;
  siteId: string;
  name: string;
  /** Variantes d'écriture ; préfiltre gratuit avant tout appel LLM. */
  aliases: string[];
  enabled: boolean;
  createdAt: string;
}

export type CompetitorInput = Pick<Competitor, 'name'> &
  Partial<Pick<Competitor, 'aliases' | 'enabled'>>;

/** `complaint` = à combler chez nous · `praise` = à égaler. */
export type MentionSentiment = 'complaint' | 'praise' | 'comparison' | 'neutral';

export type MentionAspect =
  | 'pricing'
  | 'reliability'
  | 'features'
  | 'support'
  | 'ux'
  | 'integration'
  | 'other';

/** Ce qu'une discussion dit d'un concurrent, extrait par le LLM. */
export interface CompetitorMention {
  id: string;
  competitorId: string;
  hitId: string;
  sentiment: MentionSentiment;
  aspect: MentionAspect;
  /** Citation textuelle : rend l'insight vérifiable sans rouvrir la discussion. */
  quote: string | null;
  summary: string;
  createdAt: string;
}

/** Une mention, enrichie de quoi remonter à la source. */
export interface CompetitorMentionDetail extends CompetitorMention {
  competitorName: string;
  hitTitle: string;
  hitUrl: string;
  hitPostedAt: string;
}

export interface AspectBreakdown {
  aspect: MentionAspect;
  complaints: number;
  praises: number;
}

/** Synthèse par concurrent : c'est l'écran qui répond à « quoi construire ». */
export interface CompetitorInsight {
  competitorId: string;
  name: string;
  mentions: number;
  complaints: number;
  praises: number;
  comparisons: number;
  byAspect: AspectBreakdown[];
  /** Reproches les plus récents : les manques à combler. */
  topComplaints: CompetitorMentionDetail[];
  /** Éloges les plus récents : ce qui fait leur succès. */
  topPraises: CompetitorMentionDetail[];
}

export interface CompetitorAnalysisRun {
  hitsScanned: number;
  hitsMatched: number;
  mentionsCreated: number;
  /** Analyses ignorées car déjà faites — l'opération est rejouable. */
  skipped: number;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Studio social — animation de nos propres comptes de marque
// ---------------------------------------------------------------------------

export type SocialNetwork = 'instagram' | 'twitter' | 'linkedin';

export const SOCIAL_NETWORKS: readonly SocialNetwork[] = [
  'instagram',
  'twitter',
  'linkedin',
] as const;

/**
 * `manual` : l'outil rédige et prépare, l'humain relit puis publie lui-même.
 *            C'est le seul mode implémenté — voir `SocialPublisher` côté worker.
 * `auto`   : réservé à la phase 2. Aucun adaptateur de publication n'existe :
 *            choisir `auto` ne publie rien et ne change aucun comportement,
 *            le champ n'est là que pour que le passage à l'automatisation ne
 *            demande pas une migration de schéma.
 */
export type PublishMode = 'manual' | 'auto';

/** Un compte de marque que nous détenons. Jamais un compte tiers. */
export interface SocialAccount {
  id: string;
  siteId: string;
  network: SocialNetwork;
  /** Nom d'utilisateur public, ex. "@certfleet". */
  handle: string;
  label: string;
  /** Ton et registre propres à ce compte — hérite du site, le surcharge. */
  llmInstructions: string | null;
  publishMode: PublishMode;
  enabled: boolean;
  createdAt: string;
}

export type SocialAccountInput = Pick<SocialAccount, 'network' | 'handle' | 'label'> &
  Partial<Pick<SocialAccount, 'llmInstructions' | 'publishMode' | 'enabled'>>;

/** Contraintes de format d'un réseau : elles cadrent la rédaction et la validation. */
export interface NetworkProfile {
  label: string;
  /** Longueur maximale du corps, hors hashtags. */
  maxLength: number;
  maxHashtags: number;
  aspectRatio: '1:1' | '3:2';
  /** Dimensions demandées au générateur d'images. */
  imageSize: string;
  /** Rappel de ton, injecté dans le prompt. */
  toneHint: string;
}

export const NETWORK_PROFILES: Record<SocialNetwork, NetworkProfile> = {
  instagram: {
    label: 'Instagram',
    maxLength: 2200,
    maxHashtags: 10,
    aspectRatio: '1:1',
    imageSize: '1024x1024',
    toneHint:
      "Visuel d'abord : la légende accompagne l'image. Ton direct, phrases courtes, " +
      'première personne. Les liens ne sont pas cliquables dans la légende.',
  },
  twitter: {
    label: 'X / Twitter',
    maxLength: 280,
    maxHashtags: 2,
    aspectRatio: '3:2',
    imageSize: '1536x1024',
    toneHint:
      'Une seule idée, formulée sèchement. Pas de remplissage, pas de hashtag décoratif. ' +
      "Le lien compte dans la limite de caractères.",
  },
  linkedin: {
    label: 'LinkedIn',
    maxLength: 3000,
    maxHashtags: 5,
    aspectRatio: '3:2',
    imageSize: '1536x1024',
    toneHint:
      "Professionnel sans être corporate. Ouvre sur un constat concret, développe en " +
      'quelques lignes aérées, termine par une question ouverte. Aucun jargon de motivation.',
  },
};

export type AssetOrigin = 'generated' | 'uploaded' | 'external';

/** Un visuel attaché à un post. Les octets vivent dans R2 sauf pour `external`. */
export interface SocialAsset {
  id: string;
  siteId: string;
  origin: AssetOrigin;
  /** Renseignée uniquement si `origin === 'external'`. */
  externalUrl: string | null;
  mimeType: string;
  width: number | null;
  height: number | null;
  /** Prompt image effectivement envoyé au générateur. */
  prompt: string | null;
  /** `openai` | `gemini` | null pour un envoi manuel. */
  provider: string | null;
  altText: string | null;
  byteSize: number | null;
  createdAt: string;
}

/**
 * Cycle de vie d'un post. `published` n'est atteignable que depuis `approved`
 * ou `scheduled`, et uniquement par une action humaine explicite qui enregistre
 * l'URL de la publication faite à la main.
 */
export type SocialPostStatus =
  | 'draft'
  | 'pending_review'
  | 'approved'
  | 'scheduled'
  | 'published'
  | 'rejected';

export interface SocialPost {
  id: string;
  siteId: string;
  accountId: string;
  assetId: string | null;
  status: SocialPostStatus;
  body: string;
  hashtags: string[];
  /** Consigne libre à l'origine de la rédaction. */
  brief: string | null;
  /** Suggestion d'illustration produite par le modèle en même temps que le texte. */
  imagePrompt: string | null;
  /** Hit de l'inbox recyclé en post, le cas échéant. */
  sourceHitId: string | null;
  /** Prompt intégral : sans lui, impossible de comprendre après coup un mauvais jet. */
  promptUsed: string | null;
  model: string | null;
  /** Date de publication visée. Purement indicative : rien ne part tout seul. */
  scheduledFor: string | null;
  publishedAt: string | null;
  publishedUrl: string | null;
  /** URL taguée UTM à insérer dans le post ou la bio. Posée à la validation. */
  trackingUrl: string | null;
  utmCampaign: string | null;
  reviewNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Un post enrichi de quoi l'afficher sans requête supplémentaire. */
export interface SocialPostDetail extends SocialPost {
  accountLabel: string;
  accountHandle: string;
  network: SocialNetwork;
  siteName: string;
  asset: SocialAsset | null;
}

export interface GenerateSocialPostsRequest {
  accountId: string;
  /** Sujet ou angle demandé. */
  brief: string;
  /** Nombre de variantes à produire (1-5). */
  count?: number;
  /** Recycle une discussion de l'inbox comme matière première. */
  hitId?: string;
  instructionsOverride?: string;
}

export type SocialPostInput = Partial<
  Pick<SocialPost, 'body' | 'hashtags' | 'scheduledFor' | 'assetId' | 'reviewNotes' | 'imagePrompt'>
>;

export interface GenerateAssetRequest {
  /** Par défaut : `imagePrompt` du post. */
  prompt?: string;
  altText?: string;
}

// ---------------------------------------------------------------------------
// Canaux d'alerte
// ---------------------------------------------------------------------------

export type AlertChannelKind = 'email' | 'slack' | 'discord' | 'webpush';

export interface AlertChannel {
  id: string;
  userId: string;
  /** null = s'applique à tous les sites de l'utilisateur. */
  siteId: string | null;
  kind: AlertChannelKind;
  config: AlertChannelConfig;
  /** Score minimum pour déclencher une alerte sur ce canal. */
  minScore: number;
  enabled: boolean;
  createdAt: string;
}

export type AlertChannelConfig =
  | { kind: 'email'; to: string }
  | { kind: 'slack'; webhookUrl: string }
  | { kind: 'discord'; webhookUrl: string }
  | { kind: 'webpush'; endpoint: string; p256dh: string; auth: string };

export type AlertChannelInput = Pick<AlertChannel, 'kind' | 'config'> &
  Partial<Pick<AlertChannel, 'siteId' | 'minScore' | 'enabled'>>;

// ---------------------------------------------------------------------------
// Exécutions de collecte (observabilité)
// ---------------------------------------------------------------------------

export type PollStatus = 'ok' | 'error' | 'skipped' | 'rate_limited';

export interface PollRun {
  id: string;
  sourceId: string;
  startedAt: string;
  finishedAt: string | null;
  itemsFetched: number;
  itemsNew: number;
  itemsKept: number;
  status: PollStatus;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Dashboard / statistiques
// ---------------------------------------------------------------------------

export interface DashboardStats {
  /** Fenêtre d'agrégation, en jours. */
  windowDays: number;
  hitsDiscovered: number;
  hitsRelevant: number;
  drafted: number;
  posted: number;
  visits: number;
  signups: number;
  freeToolUses: number;
  /** posted / hitsRelevant */
  responseRate: number;
  /** visits / posted */
  visitsPerPost: number;
  bySite: SiteStats[];
  bySource: SourceStats[];
}

export interface SiteStats {
  siteId: string;
  siteName: string;
  hits: number;
  posted: number;
  visits: number;
  signups: number;
}

export interface SourceStats {
  sourceId: string;
  platformKind: PlatformKind;
  sourceValue: string;
  hits: number;
  kept: number;
  posted: number;
  visits: number;
  /** kept / hits — mesure la qualité du ciblage de cette source. */
  precision: number;
}

// ---------------------------------------------------------------------------
// Résultat brut d'un collecteur (interface interne, avant persistance)
// ---------------------------------------------------------------------------

export interface CollectedItem {
  externalId: string;
  url: string;
  title: string;
  body: string | null;
  author: string | null;
  postedAt: string;
}

export interface CollectorResult {
  items: CollectedItem[];
  /** Nouveau curseur à persister sur la source. */
  nextCursor: string | null;
}

// ---------------------------------------------------------------------------
// Erreurs API
// ---------------------------------------------------------------------------

export interface ApiError {
  error: string;
  message: string;
  details?: unknown;
}
